// IPO sequence — M2 (ARCHITECTURE_M2.md §5). Design §3 Stages 3–4: the window is
// a content curve (the macro cycle's capital-markets consumer), eligibility is
// era-dependent (revenue bar + profitability discipline that collapses in the
// bubble and returns with SOX), and the process is S-1 → roadshow → pricing with
// a window-slam pull risk the whole way — file in Q1 2000 and find out.

import type { TickContext } from "../ctx.js";
import type { GameState, IpoDef, UnderwriterTier } from "../types.js";
import { charge, emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { clamp } from "../prng.js";
import { refreshFounderShare, totalShares, ttmEarnings } from "./finance.js";
import { moraleHit } from "./talent.js";

/** Consecutive profitable calendar quarters ending at the last completed quarter
 *  (public formula — bots and the UI eligibility checklist mirror it). */
export function profitableQuarters(kpiHistory: GameState["kpiHistory"]): number {
  // group completed samples into calendar quarters (month % 3 == 2 ends one)
  let count = 0;
  let i = kpiHistory.length - 1;
  while (i >= 0 && kpiHistory[i]!.month % 3 !== 2) i--; // walk back to a quarter end
  while (i >= 2 && count < 8) {
    const q = kpiHistory.slice(i - 2, i + 1);
    if (q.length < 3 || q[2]!.month - q[0]!.month !== 2) break;
    const profit = q.reduce((a, k) => a + k.revenue - k.cogs - k.opex, 0);
    if (profit <= 0) break;
    count++;
    i -= 3;
  }
  return count;
}

export interface IpoEligibility {
  ok: boolean;
  window: number;
  windowOk: boolean;
  ttmRevenue: number;
  minTtmRevenue: number;
  revenueOk: boolean;
  profitQuarters: number;
  reqProfitQuarters: number;
  profitOk: boolean;
}

/** Era-dependent S-1 eligibility (public formula, used by command validation, bots, UI).
 *  Accepts the full state or a PlayerView-shaped object (same public fields). */
export function ipoEligibility(
  state: Pick<GameState, "monthIndex" | "kpiHistory"> & { finance: { ttmRevenue: number[] } },
  ipo: Pick<IpoDef, "window" | "minTtmRevenue" | "reqProfitQuarters" | "windowFloorToFile">,
): IpoEligibility {
  const m = state.monthIndex;
  const window = interpolate(ipo.window, m);
  const minTtm = interpolate(ipo.minTtmRevenue, m);
  const reqQ = Math.round(interpolate(ipo.reqProfitQuarters, m));
  const ttm = state.finance.ttmRevenue.reduce((a, b) => a + b, 0);
  const profQ = profitableQuarters(state.kpiHistory);
  const windowOk = window >= ipo.windowFloorToFile;
  const revenueOk = ttm >= minTtm;
  const profitOk = profQ >= reqQ;
  return {
    ok: windowOk && revenueOk && profitOk,
    window,
    windowOk,
    ttmRevenue: ttm,
    minTtmRevenue: minTtm,
    revenueOk,
    profitQuarters: profQ,
    reqProfitQuarters: reqQ,
    profitOk,
  };
}

/** Fair equity value per the street model — shared by the pricing range and the
 *  monthly trading model so listing day has no discontinuity. */
export function fairEquityValue(ctx: TickContext, credibility: number, multBonus: number): number {
  const { s, content } = ctx;
  const st = content.economy.street;
  const m = s.monthIndex;
  const ttm = s.finance.ttmRevenue.reduce((a, b) => a + b, 0);
  const sentF = st.sentimentBase + st.sentimentSlope * interpolate(content.economy.sentiment, m);
  const credF = st.credBase + st.credSlope * credibility;
  // the bigger story prices the deal: sales, or earnings for the profitable
  const story = Math.max(
    interpolate(st.psMultiple, m) * ttm,
    interpolate(st.peMultiple, m) * Math.max(0, ttmEarnings(s.kpiHistory)),
  );
  return story * multBonus * sentF * credF + Math.max(0, s.cash);
}

export function processIpo(ctx: TickContext): void {
  const { s, content } = ctx;
  const cfg = content.economy.ipo;
  const ipo = s.ipo;
  const m = s.monthIndex;

  if (ipo.cooldownMonths > 0) ipo.cooldownMonths = Math.max(0, ipo.cooldownMonths - ctx.monthsPerTick);

  // lockup expiry: six months of pent-up insider supply hits the tape once
  if (ipo.lockupMonth !== null && m >= ipo.lockupMonth && s.street) {
    s.street.sharePrice *= cfg.lockupPressure;
    ipo.lockupMonth = null;
    emit(ctx, "lockupExpired", "Lockup expired — insiders can finally sell, and some do.", {
      pressure: cfg.lockupPressure,
      price: s.street.sharePrice,
    });
  }

  const proc = ipo.process;
  if (!proc) return;

  // the window can slam shut mid-process (the Q1-2000 lesson)
  const window = interpolate(cfg.window, m);
  if (window < cfg.pullFloor) {
    ipo.process = null;
    ipo.pulls += 1;
    ipo.cooldownMonths = cfg.cooldownMonths;
    s.stage = "growth";
    emit(
      ctx,
      "ipoPulled",
      "IPO pulled — the window slammed shut mid-process. The bankers stopped returning calls.",
      { window, pulls: ipo.pulls, phase: proc.phase },
    );
    return;
  }

  proc.monthsLeft -= ctx.monthsPerTick;
  switch (proc.phase) {
    case "s1":
      if (proc.monthsLeft <= 0) {
        proc.phase = "roadshow";
        proc.monthsLeft = cfg.roadshowMonths;
        emit(
          ctx,
          "roadshowStarted",
          "S-1 is effective — the roadshow begins. Management is on planes; engineering will feel it.",
          { months: cfg.roadshowMonths, productivityHit: cfg.roadshowProductivityHit },
        );
      }
      break;
    case "roadshow":
      if (proc.monthsLeft <= 0) {
        proc.phase = "pricing";
        proc.monthsLeft = cfg.pricingMaxMonths;
        const uw = cfg.underwriters[proc.underwriter];
        const perShare = fairEquityValue(ctx, uw.credStart, uw.multBonus) / totalShares(s.finance);
        proc.rangeLo = Math.max(0.01, perShare * (1 - cfg.pricingRangeSpread));
        proc.rangeHi = Math.max(0.02, perShare * (1 + cfg.pricingRangeSpread));
        emit(
          ctx,
          "ipoPricing",
          `Pricing night: the book supports $${proc.rangeLo.toFixed(2)}–$${proc.rangeHi.toFixed(2)} per share. Price the range — pop or proceeds.`,
          { rangeLo: proc.rangeLo, rangeHi: proc.rangeHi, monthsToDecide: cfg.pricingMaxMonths },
        );
      }
      break;
    case "pricing":
      if (proc.monthsLeft <= 0) {
        // management dithered; the bankers price the mid for you
        priceIpo(ctx, (proc.rangeLo! + proc.rangeHi!) / 2);
      }
      break;
  }
}

/** Execute the pricing decision: sell primary shares, book proceeds, go public. */
export function priceIpo(ctx: TickContext, perShareRaw: number): void {
  const { s, content } = ctx;
  const cfg = content.economy.ipo;
  const st = content.economy.street;
  const proc = s.ipo.process!;
  const fin = s.finance;
  const m = s.monthIndex;

  const price = clamp(perShareRaw, proc.rangeLo!, proc.rangeHi!);
  const uw = cfg.underwriters[proc.underwriter];
  const pre = totalShares(fin);
  const newShares = Math.round((cfg.newSharesFrac / (1 - cfg.newSharesFrac)) * pre);
  const gross = price * newShares;
  const fees = uw.feePct * gross;
  fin.publicShares += newShares;
  refreshFounderShare(ctx);
  charge(ctx, "ipoProceeds", gross, `IPO: ${newShares.toLocaleString()} shares at $${price.toFixed(2)}`);
  charge(ctx, "ipoFees", -fees, `underwriting fees (${(uw.feePct * 100).toFixed(1)}%)`);
  ctx.financingInflow += Math.round(gross) - Math.round(fees);

  // day one: underpricing pops (analysts love you), overpricing breaks (they don't)
  const mid = (proc.rangeLo! + proc.rangeHi!) / 2;
  const window = interpolate(cfg.window, m);
  const popMean = cfg.popCoeff * ((mid - price) / mid) + cfg.windowHeatPop * Math.max(0, window - 0.6);
  const firstDayReturn = clamp(ctx.rng.normal(popMean, cfg.firstDaySigma), -0.35, 2.0);
  const openPrice = Math.max(0.01, price * (1 + firstDayReturn));

  const total = totalShares(fin);
  s.street = {
    sharePrice: openPrice,
    credibility: clamp(uw.credStart + (firstDayReturn < 0 ? -0.08 : 0.03), 0.05, 1),
    guidancePolicy: 0,
    guidedQuarterRevenue: null,
    lastQuarterRevenue: 0,
    consecutiveMisses: 0,
    belowListingMonths: 0,
    priceHistory: [{ m, price: openPrice, cap: openPrice * total }],
  };
  s.stage = "public";
  s.ipo.process = null;
  s.ipo.ipoMonth = m;
  s.ipo.ipoPrice = price;
  s.ipo.lockupMonth = m + cfg.lockupMonths;
  // everyone in the building just watched their paper get a ticker (M3 §9)
  moraleHit(ctx, ctx.content.economy.talent.ipoMoraleBoost);

  emit(
    ctx,
    "ipoPriced",
    `IPO priced at $${price.toFixed(2)} — ${firstDayReturn >= 0 ? `day one +${(firstDayReturn * 100).toFixed(0)}%` : `broke issue, ${(firstDayReturn * 100).toFixed(0)}%`}. ` +
      `Net proceeds $${((gross - fees) / 1e6).toFixed(1)}M; market cap $${((openPrice * total) / 1e6).toFixed(0)}M. You are public.`,
    {
      price,
      firstDayReturn,
      netProceeds: Math.round(gross - fees),
      marketCap: Math.round(openPrice * total),
      underwriter: proc.underwriter,
      founderShare: fin.founderShare,
    },
  );
}

export function fileS1(ctx: TickContext, underwriter: UnderwriterTier): string | null {
  const { s, content } = ctx;
  const cfg = content.economy.ipo;
  if (s.stage !== "growth") return `S-1 requires the growth stage (currently ${s.stage})`;
  if (s.ipo.process) return "an IPO process is already underway";
  if (s.ipo.cooldownMonths > 0) return "the street remembers the pulled IPO (cooldown active)";
  if (!(underwriter in cfg.underwriters)) return "unknown underwriter tier";
  const elig = ipoEligibility(s, cfg);
  if (!elig.windowOk) return `the IPO window is shut (${elig.window.toFixed(2)} < ${cfg.windowFloorToFile})`;
  if (!elig.revenueOk) {
    return `underwriters want $${(elig.minTtmRevenue / 1e6).toFixed(0)}M TTM revenue (have $${(elig.ttmRevenue / 1e6).toFixed(1)}M)`;
  }
  if (!elig.profitOk) {
    return `underwriters want ${elig.reqProfitQuarters} profitable quarters (have ${elig.profitQuarters})`;
  }
  charge(ctx, "s1Costs", -interpolate(cfg.s1Cost, s.monthIndex), "S-1 drafting: lawyers, auditors, printers");
  s.ipo.process = { phase: "s1", monthsLeft: cfg.s1Months, underwriter, rangeLo: null, rangeHi: null };
  s.stage = "ipo";
  emit(
    ctx,
    "s1Filed",
    `S-1 filed with ${underwriter === "bulge" ? "a bulge-bracket syndicate" : "a boutique underwriter"}. Quiet period begins.`,
    { underwriter, window: elig.window },
  );
  return null;
}
